import {
  Legend,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import type { ForumHealthMetric } from '../../types';
import { CHART_COLORS, RADAR_METRICS, formatMetricAvg, type CategoryTrendShape } from './constants';

// Line-chart alternative to the radar: the SAME per-category values, plotted
// across recent months instead of month-over-month — some facilitators find
// trends easier to read as a line than a spider chart. Both metrics are drawn
// per category (State solid, Direction dotted, same color) rather than
// switching between them, normalized to a shared 0-1 scale for plotting only
// — the tooltip still reports each metric's true value/label. Categories
// hidden via the chips (shared with the radar) are excluded here too.
export default function DualMetricCategoryTrendChart({
  stateTrend,
  directionTrend,
  hiddenCategories,
  emptyLabel,
}: {
  stateTrend: CategoryTrendShape | null;
  directionTrend: CategoryTrendShape | null;
  hiddenCategories: Set<string>;
  emptyLabel: string;
}) {
  const allCategories = Array.from(new Set([
    ...(stateTrend?.categories.map((c) => c.category) ?? []),
    ...(directionTrend?.categories.map((c) => c.category) ?? []),
  ]));
  const visibleCategories = allCategories.filter((c) => !hiddenCategories.has(c));
  const monthLabels = stateTrend?.month_labels ?? directionTrend?.month_labels ?? [];
  const stateByCategory = new Map(
    (stateTrend?.categories ?? []).map((c) => [c.category, c.values] as const)
  );
  const directionByCategory = new Map(
    (directionTrend?.categories ?? []).map((c) => [c.category, c.values] as const)
  );
  const hasAnyValue = visibleCategories.some((cat) => (
    (stateByCategory.get(cat)?.some((v) => v != null)) ||
    (directionByCategory.get(cat)?.some((v) => v != null))
  ));

  if (!stateTrend && !directionTrend) {
    return (
      <div className="h-80 flex items-center justify-center">
        <p className="text-sm text-gray-400 italic text-center max-w-xs">{emptyLabel}</p>
      </div>
    );
  }
  if (allCategories.length > 0 && visibleCategories.length === 0) {
    return (
      <div className="h-80 flex items-center justify-center">
        <p className="text-sm text-gray-400 italic text-center max-w-xs">
          All areas hidden — turn an area back on below to see the trend.
        </p>
      </div>
    );
  }
  if (!hasAnyValue) {
    return (
      <div className="h-80 flex items-center justify-center">
        <p className="text-sm text-gray-400 italic text-center max-w-xs">{emptyLabel}</p>
      </div>
    );
  }

  const chartData = monthLabels.map((label, i) => {
    const point: Record<string, unknown> = { month: label };
    visibleCategories.forEach((cat) => {
      const stateVal = stateByCategory.get(cat)?.[i] ?? null;
      const directionVal = directionByCategory.get(cat)?.[i] ?? null;
      point[`${cat}__state`] = stateVal != null ? stateVal / RADAR_METRICS.state.max : null;
      point[`${cat}__state__raw`] = stateVal;
      point[`${cat}__direction`] = directionVal != null
        ? directionVal / RADAR_METRICS.direction.max
        : null;
      point[`${cat}__direction__raw`] = directionVal;
    });
    return point;
  });

  return (
    <div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="month" tick={{ fill: '#4b5563', fontSize: 12 }} />
            <YAxis domain={[0, 1]} hide />
            <Tooltip
              formatter={(_value: unknown, _name: unknown, item: { dataKey?: unknown; payload?: Record<string, unknown> }) => {
                const dataKey = String(item?.dataKey ?? '');
                const metric: ForumHealthMetric = dataKey.endsWith('__direction') ? 'direction' : 'state';
                const raw = item?.payload?.[`${dataKey}__raw`] as number | null | undefined;
                return formatMetricAvg(raw ?? null, metric);
              }}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '12px',
              }}
            />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
            {visibleCategories.flatMap((cat, i) => [
              <Line
                key={`${cat}-state`}
                type="monotone"
                dataKey={`${cat}__state`}
                name={cat}
                stroke={CHART_COLORS[i % CHART_COLORS.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                connectNulls
              />,
              <Line
                key={`${cat}-direction`}
                type="monotone"
                dataKey={`${cat}__direction`}
                name={cat}
                legendType="none"
                stroke={CHART_COLORS[i % CHART_COLORS.length]}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                connectNulls
              />,
            ])}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-gray-500 mt-2 bg-off-white rounded-lg px-3 py-2">
        <span className="font-semibold text-navy">Reading this chart:</span> each color is one
        area. The <span className="font-medium">solid line</span> is its State — how healthy that
        area is right now ({RADAR_METRICS.state.labels.map((l, i) => `${i + 1} ${l}`).join(' · ')}).
        The <span className="font-medium">dotted line</span> is its Direction — which way it&apos;s
        heading ({RADAR_METRICS.direction.labels.map((l, i) => `${i + 1} ${l}`).join(' · ')}).
        Skipped areas are excluded.
      </p>
    </div>
  );
}
